import * as React from "react";
import { useMutation } from "react-query";
import { ClientsService, CustomFieldDefinition, CustomFieldDefinitionCategory } from "gen/clients";
import * as Yup from "yup";
import { Formik } from "formik";
import { DrawerHeader } from "../../../../../components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "../../../../../components/DrawerBody/DrawerBody";
import Drawer from "@mui/material/Drawer";
import { CustomFieldForm, FieldName, FormValues } from "./components/CustomFieldForm/CustomFieldForm";
import { SelectOption, SelectOptionGroup } from "@mui/base";
import { SnackbarApiError } from "../../../../../components/SnackbarApiError/SnackbarApiError";
import { CustomFieldUtils } from "./CustomFieldUtils";

interface Props {
  clientId: string;
  category: CustomFieldDefinitionCategory;
  customFieldDefinitions: CustomFieldDefinition[];
  open: boolean;
  mappingOptions: SelectOptionGroup<string>[];
  otherFieldsForVisibility?: {
    fieldName: string;
    possibleValues?: SelectOption<string>[];
  }[];
  onClose: () => void;
  onSuccess: () => void;
}

const CreateCustomFieldPanel: React.FC<Props> = ({
  clientId,
  category,
  customFieldDefinitions,
  open,
  mappingOptions,
  otherFieldsForVisibility,
  onSuccess,
  onClose
}) => {
  const { mutate, error } = useMutation({
    onSuccess,
    mutationFn: ClientsService.updateCustomFieldDefinitions
  });
  const [options, setOptions] = React.useState<SelectOption<string>[]>([]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Required"),
    mapping: Yup.mixed().test("is-not-empty", "Required", value => (Array.isArray(value) ? value.length > 0 : !!value))
  });

  const validate = React.useCallback(
    (formValues: FormValues) => {
      const errors: Record<string, string> = {};
      if (customFieldDefinitions.find(cfd => cfd.name === formValues.name.trim())) {
        errors.name =
          "A field with this name already exists. Name must be unique across all Project and User custom fields.";
      }
      if (CustomFieldUtils.requiresOptions(formValues.type) && options.length === 0) {
        errors.options = "At least one option is required for this field type.";
      }
      return errors;
    },
    [customFieldDefinitions, options]
  );

  const onSubmit = React.useCallback(
    (formValues: FormValues) => {
      const projectCustomFieldDefinitions: CustomFieldDefinition[] = customFieldDefinitions || [];
      projectCustomFieldDefinitions.push({
        category,
        name: formValues.name.trim(),
        type: formValues.type,
        required: formValues.required,
        options: options.length > 0 ? options.map(o => ({ label: o.label as string, value: o.value })) : undefined,
        defaultValue: formValues.defaultValue.trim() || undefined,
        mapping: formValues.mapping || undefined,
        visible: formValues.visible === "VISIBLE",
        visibleIf:
          formValues[FieldName.VISIBLE] === "CONDITIONAL"
            ? [
                {
                  fieldName: formValues[FieldName.VISIBLE_IF_FIELD_NAME],
                  value: formValues[FieldName.VISIBLE_IF_FIELD_VALUE]
                }
              ]
            : [],
        validationRegex: formValues.validationRegex.trim() || undefined,
        validationMessage: formValues.validationMessage.trim() || undefined,
        description: formValues.description.trim() || undefined
      });
      mutate({
        clientId,
        requestBody: projectCustomFieldDefinitions
      });
    },
    [customFieldDefinitions, category, options, mutate, clientId]
  );

  return (
    <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 800 } }}>
      <DrawerHeader onClose={onClose} title="Add Custom Field" />
      <DrawerBody>
        {!!error && <SnackbarApiError error={error} />}
        <Formik
          initialValues={{
            [FieldName.NAME]: "",
            [FieldName.TYPE]: CustomFieldDefinition.type.INPUT_TEXT,
            [FieldName.REQUIRED]: false,
            [FieldName.DEFAULT_VALUE]: "",
            [FieldName.MAPPING]: [],
            [FieldName.VISIBLE]: "VISIBLE",
            [FieldName.VISIBLE_IF_FIELD_NAME]: "",
            [FieldName.VISIBLE_IF_FIELD_VALUE]: "",
            [FieldName.VALIDATION_REGEX]: "",
            [FieldName.VALIDATION_MESSAGE]: "",
            [FieldName.DESCRIPTION]: ""
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validate={validate}
        >
          <CustomFieldForm
            options={options}
            setOptions={setOptions}
            onCancel={onClose}
            mappingOptions={mappingOptions}
            allowTextArea={category === CustomFieldDefinitionCategory.PROJECT}
            otherFieldsForVisibility={otherFieldsForVisibility}
          />
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { CreateCustomFieldPanel };
